// A format's {slots} and the hints that describe them.
//
// The server is the authority on both: it parses the skeleton with buildSheet's
// own regex, 400s when a slot has no hint, and silently prunes hints for slots
// the skeleton no longer references. This module exists so the editor can show
// all three of those outcomes BEFORE the operator presses save, rather than
// after — but it never blocks the save, because this regex is a copy of the
// server's and a client gate built on a copy blocks legitimate edits when the
// two drift.

/**
 * The slot syntax buildSheet.js parses. Both brace and bracket forms appear in
 * practice; two alternatives rather than a character class on each side, because
 * the class form also matched mismatched pairs like "{slot]".
 */
const SLOT_RE = /\{([a-z0-9_]+)\}|\[([a-z0-9_]+)\]/gi;

/** The unique slots a skeleton references, in first-appearance order. */
export function skeletonSlots(skeleton: string): string[] {
  const slots: string[] = [];
  const seen = new Set<string>();
  for (const match of String(skeleton || '').matchAll(SLOT_RE)) {
    const slot = match[1] || match[2];
    if (!seen.has(slot)) {
      seen.add(slot);
      slots.push(slot);
    }
  }
  return slots;
}

/** slotHints as the editable one-per-line "slot: hint" text. */
export function hintsToText(slotHints: Record<string, string>): string {
  return Object.entries(slotHints || {}).map(([slot, hint]) => `${slot}: ${hint}`).join('\n');
}

/** Parse the hints text back: split each non-empty line on its FIRST colon. */
export function parseHints(text: string): Record<string, string> {
  const hints: Record<string, string> = {};
  for (const line of String(text || '').split('\n')) {
    if (!line.trim()) continue;
    const idx = line.indexOf(':');
    // A colonless line still becomes an (empty-hinted) entry so it is reported
    // as missing a hint instead of silently vanishing.
    const slot = (idx === -1 ? line : line.slice(0, idx)).trim();
    const hint = idx === -1 ? '' : line.slice(idx + 1).trim();
    if (slot) hints[slot] = hint;
  }
  return hints;
}

export interface SlotDiff {
  /** In the skeleton with no hint (or only a blank one) — the server will reject the save. */
  missing: string[];
  /** A hint for a slot the skeleton no longer has — the server drops it. */
  orphan: string[];
}

/** What the server will make of this skeleton/hints pair. */
export function diffSlots(skeleton: string, hints: Record<string, string>): SlotDiff {
  const slots = skeletonSlots(skeleton);
  const slotSet = new Set(slots);
  const hintKeys = Object.keys(hints || {});
  const hinted = new Set(hintKeys.filter(slot => String(hints[slot] || '').trim()));

  return {
    missing: slots.filter(slot => !hinted.has(slot)),
    orphan: hintKeys.filter(slot => !slotSet.has(slot)),
  };
}

/** Hint text with a blank line added for each slot that has none — appends, never overwrites. */
export function addMissingHintLines(text: string, missing: string[]): string {
  if (!missing.length) return text;
  const additions = missing.map(slot => `${slot}: `).join('\n');
  return text.trim() ? `${text.replace(/\n+$/, '')}\n${additions}` : additions;
}

/** Hint text with the orphan lines removed. */
export function removeOrphanHintLines(text: string, orphan: string[]): string {
  if (!orphan.length) return text;
  const drop = new Set(orphan);
  return String(text || '')
    .split('\n')
    .filter((line) => {
      if (!line.trim()) return true;
      const idx = line.indexOf(':');
      const slot = (idx === -1 ? line : line.slice(0, idx)).trim();
      return !drop.has(slot);
    })
    .join('\n');
}
